import { Result, Context, Uuid } from "CoreUtils";
import { schema } from "GenAlphaSchema";

//
// # Depot Tree Entity Types
//
// Types for the hierarchical depot tree structure.
// A depot path resolves to either a LibraryEntity (directory) or DesignEntity (leaf).
//

//
// # Design Entity
//
// A leaf node representing a design with content.
// Maps to a UUID that points to the actual .genalpha.json.gz file.
//
export interface DesignEntity {
  readonly kind: "design";
  readonly uuid: Uuid;
}

//
// # Library Entity
//
// An intermediate node representing a library/directory.
// Contains children but no content of its own.
//
export interface LibraryEntity {
  readonly kind: "library";
  readonly children: Map<string, DepotEntity>;
}

//
// # Depot Entity
//
// Discriminated union - a node is either a library or a design, never both.
//
export type DepotEntity = LibraryEntity | DesignEntity;

//
// # Editable Identifiers
//
// The subset/ subtype of content-identifiers that can be edited/ saved/ deleted.
// Notably absent:
// - Builtins, which the system provides and are read-only.
// - UUIDs, which are also read only, subject to potential deprecation per #982.
//
export type EditableIdent =
  | schema.HierPathIdent // Internal to Depot storage
  | schema.LibCellViewIdent; // Routes to attached OA database

export type Delete = EditableIdent;
export type Save = schema.Content & { ident: EditableIdent };

//
// # Depot Tree Nodes
//
// Tree structure returned by the list-content-tree endpoint.
// Uses `nodeKind` (not `kind`) to avoid collision with `HierPathIdent.kind`.
//
export type DepotTreeNode = DepotLibraryNode | DepotDesignNode;

export interface DepotLibraryNode {
  nodeKind: "library";
  name: string;
  children: DepotTreeNode[];
}

export interface DepotDesignNode {
  nodeKind: "design";
  name: string;
  ident: schema.HierPathIdent;
  // Content metadata for filtering. Optional because designs indexed before
  // this feature was added won't have metadata until they are re-saved.
  contentKind?: schema.ContentKind;
  pdkId?: string;
  stackId?: string;
}

//
// # PDK Metadata
//
// Information about a PDK, provided by the Depot.
// This is the single source of truth for PDK name resolution.
//
export interface PdkInfo {
  // The logical PDK name (e.g., "gf12")
  name: string;
  // The depot directory name (e.g., "gf-12lp-pdk")
  depotDir: string;
  // Display name for UI (e.g., "GlobalFoundries 12LP")
  displayName?: string;
  // Description
  description?: string;
}

//
// # PDK Name Alias Resolution
//
// Resolves a potentially aliased PDK name to its canonical form by probing a
// caller-supplied lookup predicate. Handles the common "-pdk" suffix convention:
//   "asap7"     -> "asap7-pdk"  (adds suffix when that form exists)
//   "asap7-pdk" -> "asap7"      (strips suffix when that form exists)
// Returns `pdkName` unchanged when no alias can be resolved, letting the
// caller decide how to handle an unrecognised name.
//
export function resolvePdkNameAlias(
  pdkName: string,
  hasName: (name: string) => boolean
): string {
  // Direct match — most common case
  if (hasName(pdkName)) return pdkName;

  // Try common suffixes: "asap7" -> "asap7-pdk"
  const withSuffix = `${pdkName}-pdk`;
  if (hasName(withSuffix)) return withSuffix;

  // Try stripping suffix: "asap7-pdk" -> "asap7"
  if (pdkName.endsWith("-pdk")) {
    const stripped = pdkName.slice(0, -4);
    if (hasName(stripped)) return stripped;
  }

  // No alias found — return as-is and let downstream handle the miss
  return pdkName;
}

// Symmetric PDK rule for the depot compatibility filter (DesignExplorer):
// compatible unless both ids are set and differ. For the stricter paste rule
// see `canPasteSymbolIntoPdk`.
export function arePdkIdsCompatible(a?: string, b?: string): boolean {
  if (a && b && a !== b) return false;
  return true;
}

// Tolerate the "-pdk" suffix alias (e.g. "asap7" == "asap7-pdk").
function normalizePdkId(p: string): string {
  return p.endsWith("-pdk") ? p.slice(0, -4) : p;
}

// Asymmetric paste rule: a generic symbol pastes anywhere; a PDK-specific
// symbol only into a schematic of that same PDK.
export function canPasteSymbolIntoPdk(
  symbolPdkId: string | undefined,
  targetPdkId: string | undefined
): boolean {
  if (!symbolPdkId) return true;
  if (!targetPdkId) return false;
  return normalizePdkId(symbolPdkId) === normalizePdkId(targetPdkId);
}

//
// # Depot Interface
//
// Content operations require a `namespace` parameter for multi-tenant isolation.
// Use "" (empty string) for root/default namespace.
//
//
// The deployment that owns the in-git depot, at
// `testdata/depot/content/{schemaVersion}/dev/`. Local dev and evals both read
// it, via `depot_namespace` in their tenancy config.
export const DEV_DEPOT_NAMESPACE = "dev";

export interface Depot {
  content: {
    get(
      ctx: Context,
      ident: schema.Ident,
      namespace: string
    ): Promise<Result<schema.Content, Error>>;
    save(
      ctx: Context,
      content: schema.Content,
      namespace: string
    ): Promise<Result<unknown, Error>>;
    delete(
      ctx: Context,
      ident: Delete,
      namespace: string
    ): Promise<Result<unknown, Error>>;
    exists(
      ctx: Context,
      ident: schema.Ident,
      namespace: string
    ): Promise<Result<boolean>>;
    list(ctx: Context, namespace: string): Promise<Result<Array<schema.Ident>>>;
    // Search for designs matching a filter pattern.
    // Gets the tree (optionally rooted at `rootPath`) and returns hierpaths
    // whose joined path string matches the filter as a case-insensitive regex.
    search(
      ctx: Context,
      filter: string,
      rootPath: string[] | undefined,
      namespace: string
    ): Promise<Result<Array<schema.HierPathIdent>, Error>>;
    // Serialize the depot tree to DepotTreeNode[].
    // If rootPath is provided, returns the subtree rooted at that path.
    tree(
      ctx: Context,
      rootPath: string[] | undefined,
      namespace: string
    ): Promise<Result<Array<DepotTreeNode>>>;
  };
  //
  // PDK metadata and data access.
  // All methods accept logical PDK names (e.g., "gf12") and handle
  // name-to-directory resolution internally.
  //
  // namespace parameter behavior:
  //   - non-empty (e.g., "staging"): gs://bucket/pdk/{schemaVersion}/staging/
  //   - undefined/omitted or empty string (""): gs://bucket/pdk/{schemaVersion}/,
  //     which holds no depot — callers pass a deployment
  //
  pdk: {
    // Get PDK data by logical name
    get(
      pdkName: string,
      namespace?: string
    ): Promise<Result<schema.pdk.PDK, Error>>;
    // List all available PDK logical names
    list(namespace?: string): Promise<Result<Array<string>, Error>>;
    // Get metadata for a PDK (name resolution, display name, etc.)
    // Note: reads from config, not GCS, so no namespace needed
    getInfo(pdkName: string): Result<PdkInfo, Error>;
    // List all PDK metadata
    // Note: reads from config, not GCS, so no namespace needed
    listInfo(): Array<PdkInfo>;
    // Get layer lookup for a PDK (built from pdk.genalpha.json, cached)
    getLayerLookup(
      pdkName: string,
      namespace?: string
    ): Promise<Result<PDKLayerLookup, Error>>;
    // One of the PDK's typed sibling files, as stored, or `undefined` when the
    // PDK has none. Checked by its own Rust loader, not here.
    getFile(
      pdkName: string,
      file: PdkSiblingFile,
      namespace?: string
    ): Promise<Result<string | undefined, Error>>;
  };
  //
  // Stack access. Accepts logical PDK names.
  //
  // namespace parameter behavior:
  //   - non-empty (e.g., "staging"): gs://bucket/pdk/{schemaVersion}/staging/
  //   - undefined/omitted or empty string (""): gs://bucket/pdk/{schemaVersion}/,
  //     which holds no depot — callers pass a deployment
  //
  stack: {
    get(
      pdkName: string,
      stackId: schema.StackId,
      namespace?: string
    ): Promise<Result<schema.pdk.Stack, Error>>;
    list(
      pdkName: string,
      namespace?: string
    ): Promise<Result<Array<schema.StackId>, Error>>;
  };
}

/** The typed files beside a PDK's `pdk.genalpha.json` that `pdk.getFile` serves. */
export const PDK_SIBLING_FILES = [
  "cell-terminals.json",
  "device-connectivity.json",
  "well-connectivity.json",
  "device-categories.json",
] as const;
export type PdkSiblingFile = (typeof PDK_SIBLING_FILES)[number];

/** A PDK's sibling files, as stored; a file the PDK lacks is absent. */
export interface PdkSiblingFiles {
  cellTerminals?: string;
  deviceConnectivity?: string;
  wellConnectivity?: string;
  deviceCategories?: string;
}

/** The sibling files a Custom Compiler cell import binds and checks pins with. */
export async function readPdkSiblingFiles(
  pdk: Pick<Depot["pdk"], "getFile">,
  pdkName: string,
  namespace?: string
): Promise<Result<PdkSiblingFiles, Error>> {
  const files: PdkSiblingFiles = {};
  const keys: Record<PdkSiblingFile, keyof PdkSiblingFiles> = {
    "cell-terminals.json": "cellTerminals",
    "device-connectivity.json": "deviceConnectivity",
    "well-connectivity.json": "wellConnectivity",
    "device-categories.json": "deviceCategories",
  };
  for (const file of PDK_SIBLING_FILES) {
    const read = await pdk.getFile(pdkName, file, namespace);
    if (read.err) return read;
    if (read.val !== undefined) files[keys[file]] = read.val;
  }
  return Result.Ok(files);
}

/** `name` as a file `pdk.getFile` serves, or undefined if it is not one. */
export function asPdkSiblingFile(name: string): PdkSiblingFile | undefined {
  return PDK_SIBLING_FILES.find((file) => file === name);
}

// The `get` parts of the Depot interface
// Used by client code that only needs read access to depot data.
// Does not include metadata methods (getInfo, listInfo) which are server-side only.
export interface DepotGet {
  content: {
    get: (
      ctx: Context,
      ident: schema.Ident,
      namespace: string
    ) => Promise<Result<schema.Content, Error>>;
  };
  pdk: {
    get: (
      pdkName: string,
      namespace?: string
    ) => Promise<Result<schema.pdk.PDK, Error>>;
  };
  stack: {
    get: (
      pdkName: string,
      stackId: schema.StackId,
      namespace?: string
    ) => Promise<Result<schema.pdk.Stack, Error>>;
  };
}

export interface Error {
  why: string;
  data?: unknown;
}

//
// # Layer Lookup
//
// Provides fast layer/purpose lookup by name.
// Built from pdk.genalpha.json at load time and cached.
//

export interface LayerPurposeRef {
  layer: number; // GenAlpha layer number (from PDKLayer.number)
  purpose: number; // GenAlpha purpose number (from PDKLayerPurpose.number)
}

export interface PDKLayerLookup {
  // Lookup by name - returns GenAlpha layer and purpose numbers
  getByName(
    layerName: string,
    purposeName: string
  ): LayerPurposeRef | undefined;
}
